import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "physical-therapy-web-design-colorado",
  metaTitle: "Physical Therapy Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom physical therapy web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Medical practice marketing", to: "/medical-marketing-colorado" },
  ],
  currentLabel: "Physical Therapy Web Design Colorado",
  eyebrow: "Physical Therapy Web Design — Colorado",
  h1: (
    <>
      Physical Therapy <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded physical therapy websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Physical Therapy websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first physical therapy sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Physical Therapy web design in Colorado",
    body: "Physical Therapy web design is the practice of building conversion-focused, mobile-first websites for physical therapy businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with online booking above the fold." },
    { title: "Condition + treatment pages", body: "Back pain, knee, post-op, sports injury, vestibular, pelvic floor — each ranks independently." },
    { title: "Online booking integration", body: "Real-time booking integration with WebPT, Jane, or your EMR." },
    { title: "Therapist bio pages", body: "Each PT with credentials, specialties, and direct booking CTA." },
    { title: "Insurance & cash-pay clarity", body: "Accepted plans and cash-pay rates surfaced where patients actually look." },
    { title: "Schema and SEO baked in", body: "MedicalBusiness, Service, FAQPage, Review schema at launch." },
  ],
  whyThisMarket: [
    { heading: "Patients search by condition", body: "We build pages for \"lower back pain PT\", \"ACL rehab\", \"vestibular therapy\" — the actual queries." },
    { heading: "Specialty PT outranks generalists", body: "Pelvic floor, vestibular, sports, and post-op specialties rank circles around generalists with the right pages." },
    { heading: "Real-time booking doubles conversion", body: "Practices with real-time online booking convert 2x more web visitors than form-only practices." },
  ],
  relatedLinks: [
    { to: "/medical-marketing-colorado", label: "Medical practice marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a physical therapy website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a physical therapy website?", a: "Typical timeline is 4–6 weeks for a 12–20 page physical therapy site with full service and service-area pages. Larger multi-location physical therapy businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs physical therapy businesses?", a: "No — we build physical therapy sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Physical Therapy Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for physical therapy businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/physical-therapy-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
